"use client";

import { cardData } from "@/data/data";
import Link from "next/link";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ScrollReveal from "./ScrollReveal";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Keyboard, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import type { Swiper as SwiperType } from 'swiper';

interface ExpandableCardProps {
  id: string;
  title: string;
  start: number;
  end: number;
}

const ExpandableCard: React.FC<ExpandableCardProps> = ({ title, start, end, id }) => {

  const sliderRef = useRef<SwiperType | null>(null);

  const handlePrev = useCallback(() => sliderRef.current?.slidePrev?.(), []);
  const handleNext = useCallback(() => sliderRef.current?.slideNext?.(), []);


  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth < 1024);
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  return (
    <section className="sm:px-16 px-4 sm:pt-[100px] pt-10 pb-24 max-w-[1490px] w-full h-full mx-auto relative" id={id}>
      <ScrollReveal stagger={0.05} start="top 99%" end="bottom 90%">
        <h1 className="sectionTitle reveal-item">{title}</h1>

        <div className="relative mt-12 flex items-center">
          {isMobile ? (
            <div className="relative w-full">
              <Swiper
                key={`swiper-${id}`}
                // set the actual swiper instance into our ref when ready
                onSwiper={(swiper) => (sliderRef.current = swiper)}
                className="w-full aspect-3/2"
                modules={[A11y, Keyboard, Autoplay]}
                spaceBetween={20}
                slidesPerView={2}
                loop
                keyboard={{ enabled: true }}
                autoplay={{ delay: 5000, disableOnInteraction: true }}
              >
                {cardData.slice(start, end).map((e, index) => (
                  <SwiperSlide key={index}>
                    <Link
                      href={e.link}
                      target="_blank"
                      className="group card cursor-pointer w-full h-full reveal-item rounded-lg overflow-hidden 
                      shadow-lg flex flex-col gap-6"
                    >
                      <div
                        className="h-full w-full bg-cover bg-no-repeat bg-center rounded-lg relative"
                        style={{ backgroundImage: `url(${e.img})` }}
                      >
                        {e.new && (
                          <div className="w-20 h-8 bg-yellow-400 absolute top-5 left-[1px] flex justify-center 
                          items-center">
                            <p className="text-base text-black font-semibold">New</p>
                          </div>
                        )}
                        <div className="card-content h-full w-full flex flex-col justify-end">
                          <div
                            className="card-footer rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-500 shadow-lg
                            min-h-[40px] flex flex-row items-center justify-center m-4 py-2 gap-2"
                          >
                            <div className="flex flex-col items-center justify-center text-black">
                              <h2 className="text-sm font-bold">{e.downloads}</h2>
                              <p className="text-xs">Downloads</p>
                            </div>
                            <div className="h-full w-0.5 bg-black/40" />
                            <div className="flex flex-col items-center justify-center text-black">
                              <h2 className="text-sm font-bold">{e.rating}★</h2>
                              <p className="text-xs">Rating</p>
                            </div>
                          </div>
                        </div>
                      </div>
                      <p className="text-white text-center font-semibold text-lg">{e.title}</p>
                    </Link>
                  </SwiperSlide>
                ))}
              </Swiper>

              {/* Navigation buttons are bound to this instance only */}
              <div
                className="flex justify-center items-center gap-6 absolute -bottom-20 left-1/2 
                transform -translate-x-1/2 z-[1]"
                aria-hidden={false}
              >
                <button
                  onClick={handlePrev}
                  aria-label="Previous slide"
                  title="Previous slide"
                  className="text-lg bg-[#F0C869] text-black hover:bg-yellow-400 
                    active:bg-yellow-600 shadow-[0_4px_10px_0_rgba(240,200,105,0.5)] 
                    hover:shadow-[0_6px_15px_0_rgba(240,200,105,0.7)]
                    rounded-full size-[38px] md:size-[58px] cursor-pointer"
                >
                  <FontAwesomeIcon icon={faArrowLeft} />
                </button>

                <button
                  onClick={handleNext}
                  aria-label="Next slide"
                  title="Next slide"
                  className="text-lg bg-[#F0C869] text-black hover:bg-yellow-400 
                    active:bg-yellow-600 shadow-[0_4px_10px_0_rgba(240,200,105,0.5)] 
                    hover:shadow-[0_6px_15px_0_rgba(240,200,105,0.7)]
                    rounded-full size-[38px] md:size-[58px] cursor-pointer"
                >
                  <FontAwesomeIcon icon={faArrowRight} />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-5 w-full">
              {cardData.slice(start, end).map((e, index) => (
                <Link
                  key={index}
                  href={e.link}
                  target="_blank"
                  className="group card cursor-pointer w-full h-[450px] reveal-item
                    lg:basis-[25%] aspect-[3/2] rounded-lg overflow-hidden transition-all 
                    duration-500 ease-in-out shadow-lg lg:hover:basis-[60%] flex flex-col gap-6"
                >
                  <div
                    className="h-full w-full bg-cover bg-no-repeat bg-center rounded-lg relative"
                    style={{ backgroundImage: `url(${e.img})` }}
                  >
                    {e.new && (
                      <div className="w-20 h-8 bg-yellow-400 absolute top-5 left-[1px] flex justify-center items-center">
                        <p className="text-base text-black font-semibold">New</p>
                      </div>
                    )}
                    <div className="card-content h-full w-full flex flex-col justify-end">
                      <div
                        className="card-footer rounded-lg bg-gradient-to-r from-yellow-400 to-yellow-500 shadow-lg
                          min-h-[100px] flex flex-row items-center justify-center m-4 py-4 gap-2"
                      >
                        <div className="flex flex-col items-center justify-center text-black">
                          <h2 className="lg:group-hover:text-2xl text-sm font-bold transition-all duration-500">
                            {e.downloads}
                          </h2>
                          <p className="text-xs group-hover:text-lg transition-all duration-500">Downloads</p>
                        </div>
                        <div className="h-full w-0.5 bg-black/40" />
                        <div className="flex flex-col items-center justify-center text-black">
                          <h2 className="lg:group-hover:text-2xl text-sm font-bold transition-all duration-500">
                            {e.rating}★
                          </h2>
                          <p className="text-xs group-hover:text-lg transition-all duration-500">Rating</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-white text-center font-semibold text-lg group-hover:text-2xl transition-all duration-500">
                    {e.title}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </ScrollReveal>
    </section>
  );
};

export default ExpandableCard;
